import type { HexMapState } from '../../engine/render/renderer';
import {
    getFreePopulation,
    MAX_BURNERS,
    MAX_LUMBERJACKS,
    MAX_FISHERS,
    MAX_GOLD_MINERS,
    MAX_MASONS,
    MAX_MINERS,
    MAX_MINTERS,
    MAX_QUARRYMEN,
    MAX_SAWMILLERS,
    MAX_SMELTERS,
    MAX_SMOKERS,
    MAX_SMITHS,
    MAX_WEAVERS,
} from '../../engine/map/settlement';
import type { Tile } from '../../engine/hex/types';
import { AbstractModal } from '../AbstractModal';
import type { ContentBuilder } from '../ui/ContentBuilder';
import {
    ACTION__SETTLEMENT__FIRE_BURNER,
    ACTION__SETTLEMENT__FIRE_FARMER,
    ACTION__SETTLEMENT__FIRE_FISHER,
    ACTION__SETTLEMENT__FIRE_GOLD_MINER,
    ACTION__SETTLEMENT__FIRE_HUNTER,
    ACTION__SETTLEMENT__FIRE_LUMBERJACK,
    ACTION__SETTLEMENT__FIRE_MASON,
    ACTION__SETTLEMENT__FIRE_MINER,
    ACTION__SETTLEMENT__FIRE_MINTER,
    ACTION__SETTLEMENT__FIRE_QUARRYMAN,
    ACTION__SETTLEMENT__FIRE_SMELTER,
    ACTION__SETTLEMENT__FIRE_SMOKER,
    ACTION__SETTLEMENT__FIRE_WEAVER,
    ACTION__SETTLEMENT__FIRE_SAWMILLER,
    ACTION__SETTLEMENT__FIRE_SMITH,
    ACTION__SETTLEMENT__HIRE_BURNER,
    ACTION__SETTLEMENT__HIRE_FARMER,
    ACTION__SETTLEMENT__HIRE_FISHER,
    ACTION__SETTLEMENT__HIRE_GOLD_MINER,
    ACTION__SETTLEMENT__HIRE_HUNTER,
    ACTION__SETTLEMENT__HIRE_LUMBERJACK,
    ACTION__SETTLEMENT__HIRE_MASON,
    ACTION__SETTLEMENT__HIRE_MINER,
    ACTION__SETTLEMENT__HIRE_MINTER,
    ACTION__SETTLEMENT__HIRE_QUARRYMAN,
    ACTION__SETTLEMENT__HIRE_SAWMILLER,
    ACTION__SETTLEMENT__HIRE_SMELTER,
    ACTION__SETTLEMENT__HIRE_SMOKER,
    ACTION__SETTLEMENT__HIRE_SMITH,
    ACTION__SETTLEMENT__HIRE_WEAVER,
    ACTION__SETTLEMENT__SET_PRODUCTION_CAP_PREFIX,
} from '../../engine/input/actionIds';
import { t, type Language } from '../../i18n/translations';
import { getHuntTicks, getLumberCampTarget } from '../settlement/utils';
import {
    getBuildingStorage,
    getEffectiveInputCap,
    getEffectiveOutputCap,
    setBuildingInputCap,
    setBuildingOutputCap,
} from '../../engine/settlement/utils';
import type { SettlementBuildingKey } from '../../engine/settlement/types';

export const PRODUCTION_BUILDING_IDS = [
    'lumberjack',
    'sawmill',
    'charcoal',
    'blacksmith',
    'farm',
    'hunt',
    'fishing',
    'smokehouse',
    'weaver',
    'quarry',
    'masonry',
    'ironMine',
    'smelter',
    'goldMine',
    'mint',
] as const;
export type ProductionBuildingId = (typeof PRODUCTION_BUILDING_IDS)[number];

export function isProductionBuildingId(value: string): value is ProductionBuildingId {
    return PRODUCTION_BUILDING_IDS.includes(value as ProductionBuildingId);
}

type ProductionProgressEntry = {
    label: string;
    value: number;
    max: number;
};

type ProductionStorageEntry = {
    kind: 'input' | 'output';
    resourceKey: string;
    amount: number;
    localCap: number;
    userCap: number;
    capActionId: string;
};

type ProductionWorkerEntry = {
    label: string;
    hireActionId: string;
    fireActionId: string;
    canHire: boolean;
    canFire: boolean;
    plusLabel: string;
    minusLabel: string;
};

type ProductionView = {
    buildingLabel: string;
    progress: ProductionProgressEntry[];
    progressEmptyKey: string;
    storage: ProductionStorageEntry[];
    workers: ProductionWorkerEntry[];
};

export class BuildingProductionModal extends AbstractModal {
    private buildingId: ProductionBuildingId;

    constructor(
        rect: { x: number; y: number; w: number; h: number },
        tile: Tile,
        settlementId: string,
        buildingId: ProductionBuildingId,
    ) {
        super(tile, rect, settlementId);
        this.buildingId = buildingId;
        this.id = `production_modal_${settlementId}_${buildingId}`;
    }

    getHeaderLabel(state: HexMapState): string {
        const settlement = this.settlementId ? state.settlementsById[this.settlementId] : null;
        const lang = state.config.ui.language;
        const buildingLabel = getBuildingLabel(lang, this.buildingId);
        if (!settlement) return buildingLabel;
        return t(lang, 'production.modal.title', settlement.name, buildingLabel);
    }

    handleAction(actionId: string, state: HexMapState): boolean {
        if (!actionId.startsWith(ACTION__SETTLEMENT__SET_PRODUCTION_CAP_PREFIX)) return false;
        const payload = actionId.slice(ACTION__SETTLEMENT__SET_PRODUCTION_CAP_PREFIX.length);
        const [buildingId, kind, resourceKey] = payload.split(':');
        if (!isProductionBuildingId(buildingId)) return false;
        if (kind !== 'input' && kind !== 'output') return false;
        if (!resourceKey) return false;
        const settlement = this.settlementId ? state.settlementsById[this.settlementId] : null;
        if (!settlement) return false;
        const lang = state.config.ui.language;
        const label = resourceLabel(lang, resourceKey);
        const current = getProductionCap(state, settlement, buildingId, kind, resourceKey);
        const promptKey =
            kind === 'input' ? 'prompt.productionInputCap' : 'prompt.productionOutputCap';
        const next = window.prompt(t(lang, promptKey, label), `${current}`);
        if (next === null) return false;
        const parsed = Number(next);
        if (!Number.isFinite(parsed)) return false;
        const value = Math.max(0, Math.floor(parsed));
        return setProductionCap(state, settlement, buildingId, kind, resourceKey, value);
    }

    buildContent(state: HexMapState, builder: ContentBuilder): void {
        const settlement = this.settlementId ? state.settlementsById[this.settlementId] : null;
        if (!settlement) return;
        const lang = state.config.ui.language;
        const view = buildProductionView(state, settlement, this.buildingId);

        builder.section(t(lang, 'production.modal.progress'), (section) => {
            if (view.progress.length === 0) {
                section.text(view.progressEmptyKey);
                return;
            }
            view.progress.forEach((entry) => {
                section.progress(entry.label, entry.value, entry.max);
            });
        });

        builder.section(t(lang, 'production.modal.storage'), (section) => {
            if (view.storage.length === 0) {
                section.text(t(lang, 'production.modal.noStorage'));
                return;
            }
            view.storage.forEach((entry, index) => {
                const lineKey =
                    entry.kind === 'input'
                        ? 'production.modal.inputLine'
                        : 'production.modal.outputLine';
                const label = resourceLabel(lang, entry.resourceKey);
                section.text(
                    line(lineKey, lang, label, entry.amount, entry.localCap, entry.userCap),
                );
                const buttonKey =
                    entry.kind === 'input'
                        ? 'production.modal.setInputCap'
                        : 'production.modal.setOutputCap';
                section.button(entry.capActionId, t(lang, buttonKey, label), true);
                if (index < view.storage.length - 1) section.spacer(4);
            });
        });

        builder.section(t(lang, 'production.modal.workers'), (section) => {
            section.text(t(lang, 'jobs.free', getFreePopulation(settlement)));
            section.spacer(4);
            view.workers.forEach((entry, index) => {
                section.text(entry.label);
                section.button(entry.hireActionId, entry.plusLabel, entry.canHire);
                section.button(entry.fireActionId, entry.minusLabel, entry.canFire);
                if (index < view.workers.length - 1) section.spacer(4);
            });
        });
    }
}

function line(
    key: string,
    lang: Language,
    label: string,
    value: number,
    localCap: number,
    userCap: number,
): string {
    return t(lang, key, label, formatCap(value, localCap), formatUserCap(userCap, lang));
}

function formatCap(value: number, cap: number): string {
    if (cap <= 0) return `${value}`;
    return `${value}/${cap}`;
}

function formatUserCap(value: number, lang: Language): string {
    if (value <= 0) return t(lang, 'production.modal.noCap');
    return `${value}`;
}

function resourceLabel(lang: Language, key: string): string {
    const label = t(lang, `resource.${key}`);
    return label === `resource.${key}` ? key : label;
}

function getBuildingLabel(lang: Language, buildingId: ProductionBuildingId): string {
    switch (buildingId) {
        case 'lumberjack':
            return t(lang, 'buildings.label.lumberjackCamp');
        case 'sawmill':
            return t(lang, 'buildings.label.sawmill');
        case 'charcoal':
            return t(lang, 'buildings.label.charcoalPit');
        case 'blacksmith':
            return t(lang, 'buildings.label.blacksmith');
        case 'farm':
            return t(lang, 'buildings.label.farm');
        case 'hunt':
            return t(lang, 'buildings.label.huntingHut');
        case 'fishing':
            return t(lang, 'buildings.label.fishingHut');
        case 'smokehouse':
            return t(lang, 'buildings.label.smokehouse');
        case 'weaver':
            return t(lang, 'buildings.label.weaver');
        case 'quarry':
            return t(lang, 'buildings.label.quarry');
        case 'masonry':
            return t(lang, 'buildings.label.masonry');
        case 'ironMine':
            return t(lang, 'buildings.label.ironMine');
        case 'smelter':
            return t(lang, 'buildings.label.smelter');
        case 'goldMine':
            return t(lang, 'buildings.label.goldMine');
        case 'mint':
            return t(lang, 'buildings.label.mint');
        default:
            return buildingId;
    }
}

function buildProductionView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    buildingId: ProductionBuildingId,
): ProductionView {
    const lang = state.config.ui.language;
    switch (buildingId) {
        case 'lumberjack':
            return buildLumberCampView(state, settlement, lang);
        case 'sawmill':
            return buildSawmillView(state, settlement, lang);
        case 'charcoal':
            return buildCharcoalView(state, settlement, lang);
        case 'blacksmith':
            return buildBlacksmithView(state, settlement, lang);
        case 'farm':
            return buildFarmView(state, settlement, lang);
        case 'hunt':
            return buildHuntView(state, settlement, lang);
        case 'fishing':
            return buildFishingView(state, settlement, lang);
        case 'smokehouse':
            return buildSmokehouseView(state, settlement, lang);
        case 'weaver':
            return buildWeaverView(state, settlement, lang);
        case 'quarry':
            return buildQuarryView(state, settlement, lang);
        case 'masonry':
            return buildMasonryView(state, settlement, lang);
        case 'ironMine':
            return buildIronMineView(state, settlement, lang);
        case 'smelter':
            return buildSmelterView(state, settlement, lang);
        case 'goldMine':
            return buildGoldMineView(state, settlement, lang);
        case 'mint':
            return buildMintView(state, settlement, lang);
        default:
            return {
                buildingLabel: buildingId,
                progress: [],
                progressEmptyKey: t(lang, 'production.modal.noProgress'),
                storage: [],
                workers: [],
            };
    }
}

function buildLumberCampView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const target = getLumberCampTarget(state, settlement);
    const progress = target
        ? [
              {
                  label: t(lang, 'production.lumberWork', target.age),
                  value: settlement.production.lumberCamp.work,
                  max: target.ticks,
              },
          ]
        : [];
    const outputMax = Math.max(0, state.config.buildings.lumberjackCamp.output?.wood?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'lumberjack', 'wood');
    const storage = getBuildingStorage(settlement, 'lumberjack');
    return {
        buildingLabel: t(lang, 'buildings.label.lumberjackCamp'),
        progress,
        progressEmptyKey: t(lang, 'production.lumberIdle'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'wood',
                amount: storage.wood,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('lumberjack', 'output', 'wood'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.lumberjacks',
                    settlement.jobs.lumberjacks,
                    MAX_LUMBERJACKS * settlement.buildings.lumberjack.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_LUMBERJACK,
                fireActionId: ACTION__SETTLEMENT__FIRE_LUMBERJACK,
                canHire:
                    settlement.buildings.lumberjack.qty > 0 &&
                    settlement.jobs.lumberjacks <
                        MAX_LUMBERJACKS * settlement.buildings.lumberjack.qty &&
                    free > 0,
                canFire: settlement.jobs.lumberjacks > 0,
                plusLabel: t(lang, 'jobs.lumberjackPlus'),
                minusLabel: t(lang, 'jobs.lumberjackMinus'),
            },
        ],
    };
}

function buildSawmillView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const sawmillWorkMax = state.config.production.sawmillWorkPerBatch;
    const sawmillWork = Math.max(0, Math.min(settlement.production.sawmill.work, sawmillWorkMax));
    const progress =
        settlement.buildings.sawmill.qty > 0
            ? [
                  {
                      label: t(
                          lang,
                          'production.progress',
                          Math.round(sawmillWork),
                          sawmillWorkMax,
                      ),
                      value: sawmillWork,
                      max: sawmillWorkMax,
                  },
              ]
            : [];
    const inputMax = Math.max(0, state.config.buildings.sawmill.input?.wood?.max ?? 0);
    const outputMax = Math.max(0, state.config.buildings.sawmill.output?.planks?.max ?? 0);
    const inputCap = getEffectiveInputCap(settlement, state.config, 'sawmill', 'wood');
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'sawmill', 'planks');
    const storage = getBuildingStorage(settlement, 'sawmill');
    return {
        buildingLabel: t(lang, 'buildings.label.sawmill'),
        progress,
        progressEmptyKey: t(lang, 'production.modal.noProgress'),
        storage: [
            {
                kind: 'input',
                resourceKey: 'wood',
                amount: storage.wood,
                localCap: inputMax,
                userCap: inputCap,
                capActionId: buildCapActionId('sawmill', 'input', 'wood'),
            },
            {
                kind: 'output',
                resourceKey: 'planks',
                amount: storage.planks,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('sawmill', 'output', 'planks'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.sawmillers',
                    settlement.jobs.sawmillers,
                    MAX_SAWMILLERS * settlement.buildings.sawmill.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_SAWMILLER,
                fireActionId: ACTION__SETTLEMENT__FIRE_SAWMILLER,
                canHire:
                    settlement.buildings.sawmill.qty > 0 &&
                    settlement.jobs.sawmillers <
                        MAX_SAWMILLERS * settlement.buildings.sawmill.qty &&
                    free > 0,
                canFire: settlement.jobs.sawmillers > 0,
                plusLabel: t(lang, 'jobs.sawmillerPlus'),
                minusLabel: t(lang, 'jobs.sawmillerMinus'),
            },
        ],
    };
}

function buildCharcoalView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.charcoalPit.progress.map((value, index) => ({
        label: t(lang, 'production.burner', index + 1),
        value,
        max: state.config.production.charcoal.ticksPerBatch,
    }));
    const inputMax = Math.max(0, state.config.buildings.charcoalPit.input?.wood?.max ?? 0);
    const outputMax = Math.max(0, state.config.buildings.charcoalPit.output?.charcoal?.max ?? 0);
    const inputCap = getEffectiveInputCap(settlement, state.config, 'charcoalPit', 'wood');
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'charcoalPit', 'charcoal');
    const storage = getBuildingStorage(settlement, 'charcoalPit');
    return {
        buildingLabel: t(lang, 'buildings.label.charcoalPit'),
        progress,
        progressEmptyKey: t(lang, 'production.noBurners'),
        storage: [
            {
                kind: 'input',
                resourceKey: 'wood',
                amount: storage.wood,
                localCap: inputMax,
                userCap: inputCap,
                capActionId: buildCapActionId('charcoal', 'input', 'wood'),
            },
            {
                kind: 'output',
                resourceKey: 'charcoal',
                amount: storage.charcoal,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('charcoal', 'output', 'charcoal'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.burners',
                    settlement.jobs.burners,
                    MAX_BURNERS * settlement.buildings.charcoalPit.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_BURNER,
                fireActionId: ACTION__SETTLEMENT__FIRE_BURNER,
                canHire:
                    settlement.buildings.charcoalPit.qty > 0 &&
                    settlement.jobs.burners < MAX_BURNERS * settlement.buildings.charcoalPit.qty &&
                    free > 0,
                canFire: settlement.jobs.burners > 0,
                plusLabel: t(lang, 'jobs.burnerPlus'),
                minusLabel: t(lang, 'jobs.burnerMinus'),
            },
        ],
    };
}

function buildBlacksmithView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const smithConfig = state.config.production.blacksmith;
    const smithState = settlement.production.blacksmith;
    const progress = smithState.progress.map((value, index) => {
        const task = smithState.task[index];
        return {
            label:
                task === 'weapon'
                    ? t(lang, 'production.smithWeapon', index + 1)
                    : t(lang, 'production.smithTool', index + 1),
            value,
            max: task === 'weapon' ? smithConfig.weaponTicks : smithConfig.toolTicks,
        };
    });
    const inputMax = state.config.buildings.blacksmith.input || {};
    const outputMax = state.config.buildings.blacksmith.output || {};
    const storage = getBuildingStorage(settlement, 'blacksmith');
    return {
        buildingLabel: t(lang, 'buildings.label.blacksmith'),
        progress,
        progressEmptyKey: t(lang, 'production.noSmiths'),
        storage: [
            {
                kind: 'input',
                resourceKey: 'ironIngots',
                amount: storage.ironIngots,
                localCap: Math.max(0, inputMax.ironIngots?.max ?? 0),
                userCap: getEffectiveInputCap(settlement, state.config, 'blacksmith', 'ironIngots'),
                capActionId: buildCapActionId('blacksmith', 'input', 'ironIngots'),
            },
            {
                kind: 'input',
                resourceKey: 'wood',
                amount: storage.wood,
                localCap: Math.max(0, inputMax.wood?.max ?? 0),
                userCap: getEffectiveInputCap(settlement, state.config, 'blacksmith', 'wood'),
                capActionId: buildCapActionId('blacksmith', 'input', 'wood'),
            },
            {
                kind: 'input',
                resourceKey: 'leather',
                amount: storage.leather,
                localCap: Math.max(0, inputMax.leather?.max ?? 0),
                userCap: getEffectiveInputCap(settlement, state.config, 'blacksmith', 'leather'),
                capActionId: buildCapActionId('blacksmith', 'input', 'leather'),
            },
            {
                kind: 'output',
                resourceKey: 'tools',
                amount: storage.tools.length,
                localCap: Math.max(0, outputMax.tools?.max ?? 0),
                userCap: getEffectiveOutputCap(settlement, state.config, 'blacksmith', 'tools'),
                capActionId: buildCapActionId('blacksmith', 'output', 'tools'),
            },
            {
                kind: 'output',
                resourceKey: 'weapons',
                amount: storage.weapons,
                localCap: Math.max(0, outputMax.weapons?.max ?? 0),
                userCap: getEffectiveOutputCap(settlement, state.config, 'blacksmith', 'weapons'),
                capActionId: buildCapActionId('blacksmith', 'output', 'weapons'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.smiths',
                    settlement.jobs.smiths,
                    MAX_SMITHS * settlement.buildings.blacksmith.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_SMITH,
                fireActionId: ACTION__SETTLEMENT__FIRE_SMITH,
                canHire:
                    settlement.buildings.blacksmith.qty > 0 &&
                    settlement.jobs.smiths < MAX_SMITHS * settlement.buildings.blacksmith.qty &&
                    free > 0,
                canFire: settlement.jobs.smiths > 0,
                plusLabel: t(lang, 'jobs.smithPlus'),
                minusLabel: t(lang, 'jobs.smithMinus'),
            },
        ],
    };
}

function buildFarmView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.farm.progress.map((value, index) => ({
        label: t(lang, 'production.farmer', index + 1),
        value,
        max: state.config.production.farm.ticksPerFarmer,
    }));
    const outputMax = Math.max(0, state.config.buildings.farm.output?.food?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'farm', 'food');
    const storage = getBuildingStorage(settlement, 'farm');
    return {
        buildingLabel: t(lang, 'buildings.label.farm'),
        progress,
        progressEmptyKey: t(lang, 'production.noFarmers'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'food',
                amount: storage.food,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('farm', 'output', 'food'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.farmers',
                    settlement.jobs.farmers,
                    settlement.buildings.farm.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_FARMER,
                fireActionId: ACTION__SETTLEMENT__FIRE_FARMER,
                canHire:
                    settlement.buildings.farm.qty > 0 &&
                    settlement.jobs.farmers < settlement.buildings.farm.qty &&
                    free > 0,
                canFire: settlement.jobs.farmers > 0,
                plusLabel: t(lang, 'jobs.farmerPlus'),
                minusLabel: t(lang, 'jobs.farmerMinus'),
            },
        ],
    };
}

function buildHuntView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const huntTicks = getHuntTicks(state, settlement);
    const progress = settlement.production.hunt.progress.map((value, index) => ({
        label: t(lang, 'production.hunter', index + 1),
        value,
        max: huntTicks,
    }));
    const outputMaxFood = Math.max(0, state.config.buildings.huntingHut.output?.food?.max ?? 0);
    const outputMaxLeather = Math.max(
        0,
        state.config.buildings.huntingHut.output?.leather?.max ?? 0,
    );
    const storage = getBuildingStorage(settlement, 'huntingHut');
    const foodCap = getEffectiveOutputCap(settlement, state.config, 'huntingHut', 'food');
    const leatherCap = getEffectiveOutputCap(settlement, state.config, 'huntingHut', 'leather');
    return {
        buildingLabel: t(lang, 'buildings.label.huntingHut'),
        progress,
        progressEmptyKey: t(lang, 'production.noHunters'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'food',
                amount: storage.food,
                localCap: outputMaxFood,
                userCap: foodCap,
                capActionId: buildCapActionId('hunt', 'output', 'food'),
            },
            {
                kind: 'output',
                resourceKey: 'leather',
                amount: storage.leather,
                localCap: outputMaxLeather,
                userCap: leatherCap,
                capActionId: buildCapActionId('hunt', 'output', 'leather'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.hunters',
                    settlement.jobs.hunters,
                    settlement.buildings.huntingHut.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_HUNTER,
                fireActionId: ACTION__SETTLEMENT__FIRE_HUNTER,
                canHire:
                    settlement.buildings.huntingHut.qty > 0 &&
                    settlement.jobs.hunters < settlement.buildings.huntingHut.qty &&
                    free > 0,
                canFire: settlement.jobs.hunters > 0,
                plusLabel: t(lang, 'jobs.hunterPlus'),
                minusLabel: t(lang, 'jobs.hunterMinus'),
            },
        ],
    };
}

function buildFishingView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.fishing.progress.map((value, index) => ({
        label: t(lang, 'production.fisher', index + 1),
        value,
        max: state.config.production.fishing.ticksPerCatch,
    }));
    const outputMax = Math.max(0, state.config.buildings.fishingHut.output?.fish?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'fishingHut', 'fish');
    const fishingStorage = getBuildingStorage(settlement, 'fishingHut');
    return {
        buildingLabel: t(lang, 'buildings.label.fishingHut'),
        progress,
        progressEmptyKey: t(lang, 'production.noFishers'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'fish',
                amount: fishingStorage.fish,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('fishing', 'output', 'fish'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.fishers',
                    settlement.jobs.fishers,
                    MAX_FISHERS * settlement.buildings.fishingHut.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_FISHER,
                fireActionId: ACTION__SETTLEMENT__FIRE_FISHER,
                canHire:
                    settlement.buildings.fishingHut.qty > 0 &&
                    settlement.jobs.fishers < MAX_FISHERS * settlement.buildings.fishingHut.qty &&
                    free > 0,
                canFire: settlement.jobs.fishers > 0,
                plusLabel: t(lang, 'jobs.fisherPlus'),
                minusLabel: t(lang, 'jobs.fisherMinus'),
            },
        ],
    };
}

function buildSmokehouseView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.smokehouse.progress.map((value, index) => ({
        label: t(lang, 'production.smoker', index + 1),
        value,
        max: state.config.production.smokehouse.ticksPerBatch,
    }));
    const outputMax = Math.max(0, state.config.buildings.smokehouse.output?.food?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'smokehouse', 'food');
    const smokehouseStorage = getBuildingStorage(settlement, 'smokehouse');
    return {
        buildingLabel: t(lang, 'buildings.label.smokehouse'),
        progress,
        progressEmptyKey: t(lang, 'production.noSmokers'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'food',
                amount: smokehouseStorage.food,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('smokehouse', 'output', 'food'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.smokers',
                    settlement.jobs.smokers,
                    MAX_SMOKERS * settlement.buildings.smokehouse.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_SMOKER,
                fireActionId: ACTION__SETTLEMENT__FIRE_SMOKER,
                canHire:
                    settlement.buildings.smokehouse.qty > 0 &&
                    settlement.jobs.smokers < MAX_SMOKERS * settlement.buildings.smokehouse.qty &&
                    free > 0,
                canFire: settlement.jobs.smokers > 0,
                plusLabel: t(lang, 'jobs.smokerPlus'),
                minusLabel: t(lang, 'jobs.smokerMinus'),
            },
        ],
    };
}

function buildWeaverView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.weaver.progress.map((value, index) => ({
        label: t(lang, 'production.weaver', index + 1),
        value,
        max: state.config.production.weaver.ticksPerBatch,
    }));
    const outputMax = Math.max(0, state.config.buildings.weaver.output?.clothRope?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'weaver', 'clothRope');
    const weaverStorage = getBuildingStorage(settlement, 'weaver');
    return {
        buildingLabel: t(lang, 'buildings.label.weaver'),
        progress,
        progressEmptyKey: t(lang, 'production.noWeavers'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'clothRope',
                amount: weaverStorage.clothRope,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('weaver', 'output', 'clothRope'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.weavers',
                    settlement.jobs.weavers,
                    MAX_WEAVERS * settlement.buildings.weaver.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_WEAVER,
                fireActionId: ACTION__SETTLEMENT__FIRE_WEAVER,
                canHire:
                    settlement.buildings.weaver.qty > 0 &&
                    settlement.jobs.weavers < MAX_WEAVERS * settlement.buildings.weaver.qty &&
                    free > 0,
                canFire: settlement.jobs.weavers > 0,
                plusLabel: t(lang, 'jobs.weaverPlus'),
                minusLabel: t(lang, 'jobs.weaverMinus'),
            },
        ],
    };
}

function buildQuarryView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.quarry.progress.map((value, index) => ({
        label: t(lang, 'production.quarryman', index + 1),
        value,
        max: state.config.production.quarry.ticksPerStone,
    }));
    const outputMax = Math.max(0, state.config.buildings.quarry.output?.stone?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'quarry', 'stone');
    const quarryStorage = getBuildingStorage(settlement, 'quarry');
    return {
        buildingLabel: t(lang, 'buildings.label.quarry'),
        progress,
        progressEmptyKey: t(lang, 'production.noQuarrymen'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'stone',
                amount: quarryStorage.stone,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('quarry', 'output', 'stone'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.quarrymen',
                    settlement.jobs.quarrymen,
                    MAX_QUARRYMEN * settlement.buildings.quarry.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_QUARRYMAN,
                fireActionId: ACTION__SETTLEMENT__FIRE_QUARRYMAN,
                canHire:
                    settlement.buildings.quarry.qty > 0 &&
                    settlement.jobs.quarrymen < MAX_QUARRYMEN * settlement.buildings.quarry.qty &&
                    free > 0,
                canFire: settlement.jobs.quarrymen > 0,
                plusLabel: t(lang, 'jobs.quarrymanPlus'),
                minusLabel: t(lang, 'jobs.quarrymanMinus'),
            },
        ],
    };
}

function buildMasonryView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.masonry.progress.map((value, index) => ({
        label: t(lang, 'production.mason', index + 1),
        value,
        max: state.config.production.masonry.ticksPerBrick,
    }));
    const outputMax = Math.max(0, state.config.buildings.masonry.output?.bricks?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'masonry', 'bricks');
    const masonryStorage = getBuildingStorage(settlement, 'masonry');
    return {
        buildingLabel: t(lang, 'buildings.label.masonry'),
        progress,
        progressEmptyKey: t(lang, 'production.noMasons'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'bricks',
                amount: masonryStorage.bricks,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('masonry', 'output', 'bricks'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.masons',
                    settlement.jobs.masons,
                    MAX_MASONS * settlement.buildings.masonry.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_MASON,
                fireActionId: ACTION__SETTLEMENT__FIRE_MASON,
                canHire:
                    settlement.buildings.masonry.qty > 0 &&
                    settlement.jobs.masons < MAX_MASONS * settlement.buildings.masonry.qty &&
                    free > 0,
                canFire: settlement.jobs.masons > 0,
                plusLabel: t(lang, 'jobs.masonPlus'),
                minusLabel: t(lang, 'jobs.masonMinus'),
            },
        ],
    };
}

function buildIronMineView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.ironMine.progress.map((value, index) => ({
        label: t(lang, 'production.miner', index + 1),
        value,
        max: state.config.production.ironMine.ticksPerOre,
    }));
    const outputMax = Math.max(0, state.config.buildings.ironMine.output?.ironOre?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'ironMine', 'ironOre');
    const ironStorage = getBuildingStorage(settlement, 'ironMine');
    return {
        buildingLabel: t(lang, 'buildings.label.ironMine'),
        progress,
        progressEmptyKey: t(lang, 'production.noMiners'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'ironOre',
                amount: ironStorage.ironOre,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('ironMine', 'output', 'ironOre'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.miners',
                    settlement.jobs.miners,
                    MAX_MINERS * settlement.buildings.ironMine.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_MINER,
                fireActionId: ACTION__SETTLEMENT__FIRE_MINER,
                canHire:
                    settlement.buildings.ironMine.qty > 0 &&
                    settlement.jobs.miners < MAX_MINERS * settlement.buildings.ironMine.qty &&
                    free > 0,
                canFire: settlement.jobs.miners > 0,
                plusLabel: t(lang, 'jobs.minerPlus'),
                minusLabel: t(lang, 'jobs.minerMinus'),
            },
        ],
    };
}

function buildSmelterView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.smelter.progress.map((value, index) => ({
        label: t(lang, 'production.smelter', index + 1),
        value,
        max: state.config.production.smelter.ticksPerBatch,
    }));
    const outputMax = Math.max(0, state.config.buildings.smelter.output?.ironIngots?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'smelter', 'ironIngots');
    const smelterStorage = getBuildingStorage(settlement, 'smelter');
    return {
        buildingLabel: t(lang, 'buildings.label.smelter'),
        progress,
        progressEmptyKey: t(lang, 'production.noSmelters'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'ironIngots',
                amount: smelterStorage.ironIngots,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('smelter', 'output', 'ironIngots'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.smelters',
                    settlement.jobs.smelters,
                    MAX_SMELTERS * settlement.buildings.smelter.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_SMELTER,
                fireActionId: ACTION__SETTLEMENT__FIRE_SMELTER,
                canHire:
                    settlement.buildings.smelter.qty > 0 &&
                    settlement.jobs.smelters < MAX_SMELTERS * settlement.buildings.smelter.qty &&
                    free > 0,
                canFire: settlement.jobs.smelters > 0,
                plusLabel: t(lang, 'jobs.smelterPlus'),
                minusLabel: t(lang, 'jobs.smelterMinus'),
            },
        ],
    };
}

function buildGoldMineView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.goldMine.progress.map((value, index) => ({
        label: t(lang, 'production.goldMiner', index + 1),
        value,
        max: state.config.production.goldMine.ticksPerOre,
    }));
    const outputMax = Math.max(0, state.config.buildings.goldMine.output?.goldOre?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'goldMine', 'goldOre');
    const goldStorage = getBuildingStorage(settlement, 'goldMine');
    return {
        buildingLabel: t(lang, 'buildings.label.goldMine'),
        progress,
        progressEmptyKey: t(lang, 'production.noGoldMiners'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'goldOre',
                amount: goldStorage.goldOre,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('goldMine', 'output', 'goldOre'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.goldMiners',
                    settlement.jobs.goldMiners,
                    MAX_GOLD_MINERS * settlement.buildings.goldMine.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_GOLD_MINER,
                fireActionId: ACTION__SETTLEMENT__FIRE_GOLD_MINER,
                canHire:
                    settlement.buildings.goldMine.qty > 0 &&
                    settlement.jobs.goldMiners <
                        MAX_GOLD_MINERS * settlement.buildings.goldMine.qty &&
                    free > 0,
                canFire: settlement.jobs.goldMiners > 0,
                plusLabel: t(lang, 'jobs.goldMinerPlus'),
                minusLabel: t(lang, 'jobs.goldMinerMinus'),
            },
        ],
    };
}

function buildMintView(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    lang: Language,
): ProductionView {
    const free = getFreePopulation(settlement);
    const progress = settlement.production.mint.progress.map((value, index) => ({
        label: t(lang, 'production.minter', index + 1),
        value,
        max: state.config.production.mint.ticksPerBatch,
    }));
    const outputMax = Math.max(0, state.config.buildings.mint.output?.coins?.max ?? 0);
    const outputCap = getEffectiveOutputCap(settlement, state.config, 'mint', 'coins');
    const mintStorage = getBuildingStorage(settlement, 'mint');
    return {
        buildingLabel: t(lang, 'buildings.label.mint'),
        progress,
        progressEmptyKey: t(lang, 'production.noMinters'),
        storage: [
            {
                kind: 'output',
                resourceKey: 'coins',
                amount: mintStorage.coins,
                localCap: outputMax,
                userCap: outputCap,
                capActionId: buildCapActionId('mint', 'output', 'coins'),
            },
        ],
        workers: [
            {
                label: t(
                    lang,
                    'jobs.minters',
                    settlement.jobs.minters,
                    MAX_MINTERS * settlement.buildings.mint.qty,
                ),
                hireActionId: ACTION__SETTLEMENT__HIRE_MINTER,
                fireActionId: ACTION__SETTLEMENT__FIRE_MINTER,
                canHire:
                    settlement.buildings.mint.qty > 0 &&
                    settlement.jobs.minters < MAX_MINTERS * settlement.buildings.mint.qty &&
                    free > 0,
                canFire: settlement.jobs.minters > 0,
                plusLabel: t(lang, 'jobs.minterPlus'),
                minusLabel: t(lang, 'jobs.minterMinus'),
            },
        ],
    };
}

function buildCapActionId(
    buildingId: ProductionBuildingId,
    kind: 'input' | 'output',
    resourceKey: string,
): string {
    return `${ACTION__SETTLEMENT__SET_PRODUCTION_CAP_PREFIX}${buildingId}:${kind}:${resourceKey}`;
}

function resolveBuildingKey(buildingId: ProductionBuildingId): SettlementBuildingKey {
    switch (buildingId) {
        case 'charcoal':
            return 'charcoalPit';
        case 'hunt':
            return 'huntingHut';
        case 'fishing':
            return 'fishingHut';
        default:
            return buildingId;
    }
}

function getProductionCap(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    buildingId: ProductionBuildingId,
    kind: 'input' | 'output',
    resourceKey: string,
): number {
    const buildingKey = resolveBuildingKey(buildingId);
    if (kind === 'input') {
        return getEffectiveInputCap(settlement, state.config, buildingKey, resourceKey);
    }
    return getEffectiveOutputCap(settlement, state.config, buildingKey, resourceKey);
}

function setProductionCap(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    buildingId: ProductionBuildingId,
    kind: 'input' | 'output',
    resourceKey: string,
    value: number,
): boolean {
    const buildingKey = resolveBuildingKey(buildingId);
    const current =
        kind === 'input'
            ? (settlement.buildings[buildingKey].inputCap[resourceKey] ?? 0)
            : (settlement.buildings[buildingKey].outputCap[resourceKey] ?? 0);
    const next =
        kind === 'input'
            ? setBuildingInputCap(settlement, state.config, buildingKey, resourceKey, value)
            : setBuildingOutputCap(settlement, state.config, buildingKey, resourceKey, value);
    return current !== next;
}
